import { RootState } from "@/redux/store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { differenceInCalendarDays, parseISO } from "date-fns";
import {
  IHotelRecheckBody,
  IHotelRecheckBodyRoom,
} from "../types/hotel-recheck.type";
import { IHotelList } from "@/feature/hotel/search/types/hotel-search-page-type";

interface UnifiedHotelFilterState {
  selectedHotel: IHotelList | undefined;
  hotelPage: "hotel-list" | "hotel-details" | "hotel-booking";
  roomCheckInfo?: IHotelRecheckBody | undefined;
  hotelTimer?: number;
  isLoading: boolean;
}

const initialState: UnifiedHotelFilterState = {
  selectedHotel: undefined,
  hotelPage: "hotel-list",
  roomCheckInfo: undefined,
  hotelTimer: undefined,
  isLoading: false,
};

const hotelDataSlice = createSlice({
  name: "hotel_data_slice",
  initialState,
  reducers: {
    setHotelPage: (
      state,
      action: PayloadAction<"hotel-list" | "hotel-details" | "hotel-booking">,
    ) => {
      state.hotelPage = action.payload;
    },

    setSelectHotel: (state, action: PayloadAction<IHotelList>) => {
      state.selectedHotel = action.payload;
    },

    setRoomCheckInfo: (
      state,
      action: PayloadAction<{
        search_id: string;
        from_date: string;
        to_date: string;
        rooms: IHotelRecheckBodyRoom[];
      }>,
    ) => {
      const { search_id, from_date, to_date, rooms } = action.payload;
      const nights = differenceInCalendarDays(
        parseISO(to_date),
        parseISO(from_date),
      );

      state.roomCheckInfo = {
        from_date,
        to_date,
        search_id,
        nights,
        rooms,
      };
    },
    clearRoomCheckInfo: (state) => {
      state.roomCheckInfo = undefined;

      if (state.hotelPage === "hotel-booking") {
        state.hotelPage = "hotel-details";
      }
    },

    setHotelExpiredTimer: (
      state,
      action: PayloadAction<number | undefined>,
    ) => {
      state.hotelTimer = action.payload;
    },

    setHotelLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    resetHotelSessionState: () => initialState,
  },
});

export const {
  setSelectHotel,
  setHotelPage,
  setRoomCheckInfo,
  clearRoomCheckInfo,
  setHotelExpiredTimer,
  setHotelLoading,
  resetHotelSessionState,
} = hotelDataSlice.actions;

export const useSelectedHotel = (state: RootState) => state.selectedHotelData;

export default hotelDataSlice;
